import styled from "@emotion/styled";
import React from "react";
import { CaregiverComplianceItemMobile, CaregiverUploadDocumentMobile } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import {
  getComplianceCardInfo,
  getUploadButtonText,
  groupUploadsByParents,
} from "../../../utils/complianceUtils";
import { DOCUMENT_OUTLINE_ICON } from "../../../utils/svgs_new";
import { Center } from "../../Center";
import { AppButton } from "../../Generic/AppButton";
import { AppIcon } from "../../Generic/AppIcon";
import Flex from "../../Generic/Flex";
import ScaffoldTitle from "../../Scaffold/components/ScaffoldTitle";
import Scaffold from "../../Scaffold/Scaffold";
import ComplianceDocumentCard from "./ComplianceDocumentCard";
import MyMedflytComplianceScreen from "./MyMedflytComplianceScreen";
import ComplianceItemStatusCard from "./ComplianceItemStatusCard";
import { doNothing } from "../../../utils/doNothing";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../../Generic/AppCard";

interface Props {
  item: CaregiverComplianceItemMobile;
  onClickComplianceDocument: (document: CaregiverUploadDocumentMobile) => void;
  onClickUploadDocument: () => void;
  onClickGoBack: () => void;
}

function ComplianceItemScreen(props: Props) {
  const groups = groupUploadsByParents(props.item.uploads);

  return (
    <Scaffold
      title={<ScaffoldTitle label={props.item.documentType.name} />}
      onClickGoBack={props.onClickGoBack}
      footer={
        <ComplianceItemScreen.Footer>
          <ComplianceItemScreen.UploadButton
            variant="filled"
            color="primary"
            onClick={props.onClickUploadDocument}
          >
            {getUploadButtonText(props.item)}
          </ComplianceItemScreen.UploadButton>
        </ComplianceItemScreen.Footer>
      }
    >
      {groups.length === 0 ? (
        <ComplianceItemScreen.NoDocuments>
          <Flex direction="column" xGap={8} alignItems="center">
            <AppIcon icon={DOCUMENT_OUTLINE_ICON} size={64} />
            <h4>
              {props.item.documentType.name} {translate(STRINGS.SHOULD_BE_HERE)}
            </h4>
            <p>{translate(STRINGS.YOU_HAVENT_UPLOADED_ANYTHING_YET)}</p>
          </Flex>
        </ComplianceItemScreen.NoDocuments>
      ) : (
        <MyMedflytComplianceScreen.Body>
          <ComplianceItemStatusCard
            isLoading={false}
            {...getComplianceCardInfo(props.item)}
            onClick={doNothing}
          />
          {groups.map((group) => (
            <div key={group.parent}>
              <MyMedflytComplianceScreen.Section>
                <MyMedflytComplianceScreen.SectionTitle>
                  {group.parent}
                </MyMedflytComplianceScreen.SectionTitle>
                <AppCard>
                  <AppCardHeader>
                    <AppCardTitle>{translate(STRINGS.PAST_UPLOADS)}</AppCardTitle>
                  </AppCardHeader>
                  <ComplianceItemScreen.Uploads>
                    {group.uploads.map((upload) => (
                      <ComplianceDocumentCard
                        key={upload.id.toString()}
                        document={upload}
                        onClick={() => props.onClickComplianceDocument(upload)}
                      />
                    ))}
                  </ComplianceItemScreen.Uploads>
                </AppCard>
              </MyMedflytComplianceScreen.Section>
            </div>
          ))}
        </MyMedflytComplianceScreen.Body>
      )}
    </Scaffold>
  );
}

ComplianceItemScreen.Footer = styled.div`
  padding: 16px;
`;

ComplianceItemScreen.UploadButton = styled(AppButton)`
  width: 100%;
`;

ComplianceItemScreen.NoDocuments = styled(Center)`
  color: var(--gray-600);

  > div {
    padding: 0 20px;
    text-align: center;
  }

  h4 {
    margin: 0;
  }
  p {
    margin: 0;
  }
`;

ComplianceItemScreen.Uploads = styled(AppCardContent)`
  > *:not(:last-child) {
    border-bottom: 1px solid var(--gray-100);
  }
  padding: 0;
`;

export default ComplianceItemScreen;
